/** Subgráfico del tooltip del heatmap: filtra la tabla que alimenta el heatmap por el marcador. */

export const TOOLTIP_DETAIL_CHART_TYPES = [
  { value: 'intervals', label: 'Intervalos' },
  { value: 'bar', label: 'Barras' },
  { value: 'line', label: 'Líneas' },
  { value: 'pie', label: 'Torta' },
  { value: 'doughnut', label: 'Dona' },
  { value: 'radar', label: 'Radar (spider)' },
  { value: 'scatter', label: 'Dispersión' },
  { value: 'table', label: 'Tabla' },
];

const CHART_TYPE_VALUES = new Set(TOOLTIP_DETAIL_CHART_TYPES.map((t) => t.value));

export function tooltipDetailChartLabel(type) {
  return TOOLTIP_DETAIL_CHART_TYPES.find((t) => t.value === type)?.label || type || '';
}

const str = (v) => (typeof v === 'string' ? v : '');

const EPISODE_TIME_FIELDS = ['inicio', 'fin', 'hora_inicio', 'dia'];

function normalizeChartStyle(style) {
  if (!style || typeof style !== 'object') return {};
  const out = {};
  ['stacked', 'showValueLabels', 'fillTimeAxisGaps', 'showNameLabel'].forEach((key) => {
    if (style[key] === true) out[key] = true;
  });
  if (style.lineFillMissingAsZero === false) out.lineFillMissingAsZero = false;
  if (style.legendDisplay === false) out.legendDisplay = false;
  if (style.showValueLabel === false) out.showValueLabel = false;
  if (style.showPercentLabel === false) out.showPercentLabel = false;
  ['lineInterpolation', 'xAxisTitle', 'yAxisTitle', 'legendPosition', 'pieLabelPosition', 'pieLegendFontSize'].forEach(
    (key) => {
      if (typeof style[key] === 'string' && style[key]) out[key] = style[key];
    }
  );
  return out;
}

export function normalizeTooltipDetail(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const chart = raw.chart && typeof raw.chart === 'object' ? raw.chart : {};
  const type = CHART_TYPE_VALUES.has(chart.type) ? chart.type : '';
  const filterColumn = str(raw.filterColumn);
  if (!filterColumn || !type) return null;
  return {
    filterColumn,
    // El valor del marcador se compara contra la misma columna de la tabla base.
    targetColumn: filterColumn,
    chart: {
      type,
      xColumn: str(chart.xColumn),
      yColumn: str(chart.yColumn),
      labelColumn: str(chart.labelColumn),
      startColumn: str(chart.startColumn),
      endColumn: str(chart.endColumn),
      seriesColumn: str(chart.seriesColumn),
      columns: Array.isArray(chart.columns) ? chart.columns.filter((c) => typeof c === 'string') : [],
      style: normalizeChartStyle(chart.style),
    },
  };
}

/** Formatos de fecha de las columnas de la tabla, keyed por el alias de la fila. */
export function temporalMapsFromColumns(columns, aliases, { episodeDateFormat } = {}) {
  const columnDateFormats = {};
  const columnDateBuckets = {};
  (columns || []).forEach((col, i) => {
    const key = aliases?.[i];
    if (!key || !col) return;
    if (col.dateDisplayFormat && col.dateDisplayFormat !== 'auto') columnDateFormats[key] = col.dateDisplayFormat;
    if (col.dateBucket && col.dateBucket !== 'raw') columnDateBuckets[key] = col.dateBucket;
  });
  if (episodeDateFormat && episodeDateFormat !== 'auto') {
    EPISODE_TIME_FIELDS.forEach((key) => {
      columnDateFormats[key] = episodeDateFormat;
    });
  }
  return { columnDateFormats, columnDateBuckets };
}

/** Columnas marcadas "Mostrar como imagen" (ver DataTable.js), keyed por el alias de la fila. */
export function imageColumnsFromColumns(columns, aliases) {
  const columnImageColumns = {};
  (columns || []).forEach((col, i) => {
    const key = aliases?.[i];
    if (!key || !col) return;
    if (col.displayAsImage) columnImageColumns[key] = true;
  });
  return columnImageColumns;
}

export function lookupColumnTemporal(maps, column) {
  const formats = maps?.columnDateFormats || {};
  const buckets = maps?.columnDateBuckets || {};
  if (!column) return {};
  const directFormat = Object.prototype.hasOwnProperty.call(formats, column) ? formats[column] : undefined;
  const directBucket = Object.prototype.hasOwnProperty.call(buckets, column) ? buckets[column] : undefined;
  if (directFormat || directBucket) {
    return { dateDisplayFormat: directFormat, dateBucket: directBucket };
  }
  const lower = String(column).toLowerCase();
  const formatKey = Object.keys(formats).find((k) => k.toLowerCase() === lower);
  const bucketKey = Object.keys(buckets).find((k) => k.toLowerCase() === lower);
  return {
    dateDisplayFormat: formatKey ? formats[formatKey] : undefined,
    dateBucket: bucketKey ? buckets[bucketKey] : undefined,
  };
}

function styleToAdvancedConfig(chart) {
  const style = chart?.style || {};
  const type = chart?.type;
  const advanced = {};
  if (type === 'bar' || type === 'line' || type === 'scatter') {
    if (style.xAxisTitle) advanced.xAxisTitle = style.xAxisTitle;
    if (style.yAxisTitle) advanced.yAxisTitle = style.yAxisTitle;
  }
  if ((type === 'bar' || type === 'line') && style.fillTimeAxisGaps) advanced.fillTimeAxisGaps = true;
  if (type === 'bar' && style.showValueLabels) {
    advanced.barShowTotalLabel = true;
    advanced.barShowSegmentLabel = Boolean(style.stacked && chart.labelColumn);
  }
  if (type === 'line') {
    advanced.lineFillMissingAsZero = style.lineFillMissingAsZero !== false;
    advanced.lineInterpolation = style.lineInterpolation || 'linear';
    if (style.showValueLabels) advanced.lineShowValueLabel = true;
  }
  if (type === 'pie' || type === 'doughnut') {
    advanced.legendDisplay = style.legendDisplay !== false;
    advanced.legendPosition = style.legendPosition || 'left';
    advanced.pieShowValueLabel = style.showValueLabel !== false;
    advanced.pieShowPercentLabel = style.showPercentLabel !== false;
    advanced.pieShowNameLabel = style.showNameLabel === true;
    advanced.pieLabelPosition = style.pieLabelPosition || 'inside';
    if (style.pieLegendFontSize) advanced.pieLegendFontSize = style.pieLegendFontSize;
  }
  if (type === 'intervals' && style.legendDisplay === false) advanced.legendDisplay = false;
  return advanced;
}

export function isTooltipDetailConfigured(detail) {
  return Boolean(normalizeTooltipDetail(detail));
}

function findRowKey(row, column) {
  if (!row || !column) return null;
  if (Object.prototype.hasOwnProperty.call(row, column)) return column;
  const lower = String(column).toLowerCase();
  return Object.keys(row).find((k) => k.toLowerCase() === lower) || null;
}

function cellKey(value) {
  if (value == null) return '';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

/** Valores del campo del marcador; en un cluster, los de todos sus puntos. */
export function tooltipFilterValues(point, filterColumn) {
  if (!point || !filterColumn) return [];
  const members = point.members?.length ? point.members : [point];
  const out = [];
  const seen = new Set();
  members.forEach((m) => {
    const row = m?.row;
    const key = findRowKey(row, filterColumn);
    if (!key) return;
    const value = row[key];
    const k = cellKey(value);
    if (seen.has(k)) return;
    seen.add(k);
    out.push(value);
  });
  return out;
}

export function filterRowsByTooltipValues(rows, targetColumn, values) {
  if (!Array.isArray(rows) || !targetColumn || !values?.length) return [];
  const wanted = new Set(values.map(cellKey));
  return rows.filter((row) => {
    const key = findRowKey(row, targetColumn);
    return key != null && wanted.has(cellKey(row[key]));
  });
}

function displayValue(value) {
  if (value == null || value === '') return '(vacío)';
  const s = cellKey(value);
  return s.length > 40 ? `${s.slice(0, 37)}…` : s;
}

export function describeTooltipFilter(column, values) {
  if (!column || !values?.length) return '';
  if (values.length === 1) return `${column} = ${displayValue(values[0])}`;
  const shown = values.slice(0, 5).map(displayValue).join(', ');
  const more = values.length > 5 ? `, … (+${values.length - 5})` : '';
  return `${column} en (${shown}${more})`;
}

/** Props de ChartWidget para el subgráfico configurado. */
export function tooltipDetailChartProps(detail, temporal = {}) {
  const chart = detail?.chart || {};
  const advancedConfig = styleToAdvancedConfig(chart);
  if (chart.type === 'intervals') {
    const start = lookupColumnTemporal(temporal, chart.startColumn);
    const end = lookupColumnTemporal(temporal, chart.endColumn);
    return {
      type: 'intervals',
      intervalConfig: {
        startColumn: chart.startColumn,
        endColumn: chart.endColumn,
        seriesColumn: chart.seriesColumn,
        startDateDisplayFormat: start.dateDisplayFormat,
        endDateDisplayFormat: end.dateDisplayFormat,
      },
      advancedConfig,
    };
  }
  if (chart.type === 'table') {
    return {
      type: 'table',
      columns: chart.columns,
      columnDateFormats: temporal.columnDateFormats || {},
      columnDateBuckets: temporal.columnDateBuckets || {},
    };
  }
  const x = lookupColumnTemporal(temporal, chart.xColumn);
  const label = lookupColumnTemporal(temporal, chart.labelColumn);
  return {
    type: chart.type,
    xColumn: chart.xColumn,
    yColumn: chart.yColumn,
    labelColumn: chart.labelColumn || undefined,
    xDateDisplayFormat: x.dateDisplayFormat,
    xDateBucket: x.dateBucket,
    labelDateDisplayFormat: label.dateDisplayFormat,
    labelDateBucket: label.dateBucket,
    stacked: chart.type === 'bar' && chart.style?.stacked === true,
    advancedConfig,
  };
}

export function tooltipDetailHasStyleOptions(type) {
  return type === 'bar' || type === 'line' || type === 'scatter' || type === 'pie' || type === 'doughnut' || type === 'intervals';
}
